import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useGetUserProfileQuery } from '../generated-mesh/graphql';
import { useAuth } from './AuthContext';

type SatisfactionRatingContextType = {
  isNewUser: boolean;
  triggerCount: number;
  setIsNewUser: (value: boolean) => void;
  setTriggerCount: (value: number) => void;
};

const SatisfactionRatingContext = createContext<SatisfactionRatingContextType>({
  isNewUser: false,
  triggerCount: 0,
  setIsNewUser: () => { },
  setTriggerCount: () => { },
});

export const SatisfactionRatingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useAuth();
  const [{ data, error }] = useGetUserProfileQuery({
    variables: { user_uuid: user.userId },
  });

  useEffect(() => {
    if (error) {
      console.error('Failed to fetch user profile:', error);
    }
  }, [error]);

  const [isNewUser, setIsNewUser] = useState(false);
  const [triggerCount, setTriggerCount] = useState(0);
  const prevIsOnboarded = useRef<boolean | null>(null);

  // Detect when is_onboarded changes from false to true (user just completed onboarding)
  useEffect(() => {
    const currentIsOnboarded = data?.getUserProfile?.is_onboarded;
    if (prevIsOnboarded.current === false && currentIsOnboarded === true) {
      setIsNewUser(true);
      setTriggerCount(0);
    }

    prevIsOnboarded.current = currentIsOnboarded ?? null;
  }, [data?.getUserProfile?.is_onboarded, setIsNewUser, setTriggerCount]);

  return (
    <SatisfactionRatingContext.Provider
      value={{ isNewUser, triggerCount, setIsNewUser, setTriggerCount }}
    >
      {children}
    </SatisfactionRatingContext.Provider>
  );
};

export const useSatisfactionRatingContext = () => {
  const context = useContext(SatisfactionRatingContext);
  if (context === undefined) {
    throw new Error(
      'useSatisfactionRatingContext must be used within a SatisfactionRatingProvider'
    );
  }
  return context;
};
